import { useEffect, useId, useRef } from 'react';
import {
    AlertBox,
    AlertBoxDismissButton,
    Box,
    Button,
    FlexBox,
    FormField,
    Icon,
    IconButton,
    Typography,
    VisuallyHidden,
    getTokenCssVar,
} from '@vp/swan';
import type { KeyboardEvent, RefObject } from 'react';
import { FormattedReactMessage, __ } from '@99designs/i18n';
import { useChatAttachments } from '../../hooks/useChatAttachments';
import { AttachmentPreviews } from '../AttachmentPreviews';
import { ResizableTextArea } from '../ResizableTextArea';
import type { FileUploaderPort, StagedAttachment } from '../../upload';
import type { ResizableTextAreaRef } from '../ResizableTextArea';

/**
 * The Brief Experience API rejects messages longer than this with a 400
 * `invalid_request` (MAX_CHAT_MESSAGE_CHARS in brief-experience
 * src/lib/prompt/config.ts). The OpenAPI schema carries no maxLength — the limit is
 * documented in prose and enforced server-side — so it cannot be derived from the
 * generated types and must be kept in sync by hand.
 *
 * Applied as a hard `maxLength`, so the browser blocks typing and truncates paste and
 * the limit is never actually reached. That is why there is no over-limit error state.
 * For an image upload it constrains the caption alone — the base64 payload is not typed.
 */
export const MAX_MESSAGE_CHARS = 1000;

export type ChatInputProps = {
    value: string;
    onChange: (value: string) => void;
    onSend: () => void;
    onSwitchToForm?: () => void;
    canSwitchToForm?: boolean;
    /** Conversation is closed or has failed repeatedly — the field is unusable. */
    isDisabled?: boolean;
    /** A reply is being generated. The field stays editable; see below. */
    isBusy?: boolean;
    placeholder?: string;
    inputRef?: RefObject<ResizableTextAreaRef>;
    /**
     * The file-upload transport. When absent, the attach button is not rendered and the
     * composer is text-only — the chat remains fully usable without an uploader.
     */
    uploader?: FileUploaderPort;
    /** Files the customer has uploaded and staged, not yet sent. */
    attachments?: StagedAttachment[];
    onAttachmentsAdd?: (added: StagedAttachment[]) => void;
    onAttachmentRemove?: (uploadId: string) => void;
    /** Injected error reporter — passed through to useChatAttachments for delete failures. */
    onError?: (error: unknown, context: Record<string, unknown>) => void;
};

export function ChatInput({
    value,
    onChange,
    onSend,
    onSwitchToForm,
    canSwitchToForm = false,
    isDisabled = false,
    isBusy = false,
    placeholder,
    inputRef,
    uploader,
    attachments = [],
    onAttachmentsAdd,
    onAttachmentRemove,
    onError,
}: ChatInputProps) {
    const internalRef = useRef<ResizableTextAreaRef>(null);
    const textAreaRef = inputRef ?? internalRef;
    // SWAN's FormField/FormLabel wire id/htmlFor from FormFieldContext, always
    // overriding any htmlFor passed to FormLabel, and that context is only
    // populated a few effect-driven re-renders after the textarea mounts. Now that
    // ChatInput mounts asynchronously (once history has loaded), that race is lost
    // before an assistive-tech query runs — hence a plain, explicitly-linked
    // label/textarea pair instead of SWAN's FormField wiring.
    const inputId = useId();

    const attach = useChatAttachments({
        uploader,
        attachments,
        onAttachmentsAdd,
        onAttachmentRemove,
        onError,
    });

    // The parent owns the value, so growing the field is driven from it — including
    // when it is cleared on send, which must shrink the field back to one row.
    useEffect(() => {
        textAreaRef.current?.resize();
    }, [value, textAreaRef]);

    const canSend =
        (value.trim() !== '' || attachments.length > 0) &&
        !isBusy &&
        !isDisabled &&
        !attach.isUploading;

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        // isComposing guards IME input: while composing a CJK character, Enter commits
        // the candidate rather than meaning "send".
        if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;

        event.preventDefault();
        if (canSend) onSend();
    };

    return (
        // No top border by design: the composer sits on the same background as the
        // thread and is separated by whitespace, not a rule.
        <Box
            style={{
                flexShrink: 0,
                backgroundColor: getTokenCssVar('SwanSemColorBgStrong'),
            }}
        >
            <Box
                style={{
                    maxWidth: '768px',
                    margin: '0 auto',
                    padding: `${getTokenCssVar('SwanSemSpace5')} ${getTokenCssVar(
                        'SwanSemSpace5'
                    )} ${getTokenCssVar('SwanSemSpace3')}`,
                }}
            >
                {attach.lowResWarning && attachments.length > 0 && (
                    <Box mb={3} role="alert">
                        <AlertBox
                            skin="warning"
                            accessibleTextForIcon={__('Warning')}
                            onRequestDismiss={attach.dismissLowResWarning}
                        >
                            {__(
                                'One of your images looks quite small — your designer may have trouble seeing the details. Got a higher quality version?'
                            )}
                            <AlertBoxDismissButton accessibleText={__('Dismiss warning')} />
                        </AlertBox>
                    </Box>
                )}

                {attach.fileError && (
                    <Box mb={3} role="alert">
                        <AlertBox
                            skin="error"
                            accessibleTextForIcon={__('Error')}
                            onRequestDismiss={attach.dismissError}
                        >
                            {attach.fileError}
                            <AlertBoxDismissButton accessibleText={__('Dismiss error')} />
                        </AlertBox>
                    </Box>
                )}

                <FlexBox
                    role="group"
                    aria-label={__('Message input')}
                    aria-busy={isBusy}
                    flexDirection="column"
                >
                    {onSwitchToForm && canSwitchToForm && (
                        <Box
                            style={{
                                marginBottom: getTokenCssVar('SwanSemSpace5'),
                                textAlign: 'center',
                            }}
                        >
                            <Typography fontSkin="body-standard">
                                <FormattedReactMessage
                                    message={__(
                                        'Prefer not to chat? <FormLink>Fill out a form instead</FormLink>',
                                        {
                                            _context:
                                                'CTA under chat composer offering the form alternative',
                                        }
                                    )}
                                    replacements={{
                                        FormLink: <Button skin="link" onClick={onSwitchToForm} />,
                                    }}
                                />
                            </Typography>
                        </Box>
                    )}
                    <FormField>
                        <VisuallyHidden>
                            <label htmlFor={inputId}>{__('Type your message')}</label>
                        </VisuallyHidden>
                        <Box
                            style={{
                                backgroundColor: getTokenCssVar('SwanSemColorBgStandard'),
                                borderRadius: getTokenCssVar('SwanSemBorderRadiusInput'),
                                boxShadow: `inset 0 0 0 ${getTokenCssVar(
                                    'SwanSemBorderWidthInput'
                                )} ${getTokenCssVar('SwanSemColorBorderInput')}`,
                            }}
                        >
                            <AttachmentPreviews
                                attachments={attachments}
                                isUploading={attach.isUploading}
                                pendingCount={attach.pendingCount}
                                onRemove={attach.onRemove}
                            />
                            <Box style={{ position: 'relative' }}>
                                <ResizableTextArea
                                    ref={textAreaRef}
                                    id={inputId}
                                    value={value}
                                    onChange={(event) => onChange(event.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder={placeholder ?? __('Type your answer here')}
                                    maxLength={MAX_MESSAGE_CHARS}
                                    rows={1}
                                    pb={10}
                                    pr={5}
                                    pt={4}
                                    fullWidth
                                    disabled={isDisabled}
                                    style={{
                                        width: '100%',
                                        maxHeight: '55vh',
                                        resize: 'none',
                                        overflow: 'auto',
                                        boxShadow: 'none',
                                        backgroundColor: 'transparent',
                                    }}
                                />
                                <FlexBox
                                    alignItems="center"
                                    gap="between-actions"
                                    style={{
                                        position: 'absolute',
                                        right: getTokenCssVar('SwanSemSpace4'),
                                        bottom: getTokenCssVar('SwanSemSpace4'),
                                    }}
                                >
                                    {attach.enabled && (
                                        <>
                                            <input
                                                ref={attach.fileInputRef}
                                                type="file"
                                                multiple
                                                accept={attach.accept}
                                                onChange={attach.onFileChange}
                                                style={{ display: 'none' }}
                                                aria-hidden="true"
                                                tabIndex={-1}
                                                data-testid="chat-file-input"
                                            />
                                            <IconButton
                                                skin="tertiary"
                                                tooltip={__('Attach image')}
                                                aria-label={__('Attach image')}
                                                onClick={attach.openFilePicker}
                                                disabled={
                                                    isDisabled ||
                                                    isBusy ||
                                                    attach.isUploading ||
                                                    !attach.canAttachMore
                                                }
                                            >
                                                <Icon iconType="attachment" />
                                            </IconButton>
                                        </>
                                    )}
                                    <IconButton
                                        skin="primary"
                                        tooltip={__('Send message')}
                                        aria-label={__('Send message')}
                                        disabled={!canSend}
                                        onClick={onSend}
                                    >
                                        <Icon iconType="send" />
                                    </IconButton>
                                </FlexBox>
                            </Box>
                        </Box>
                    </FormField>
                </FlexBox>
                <Typography
                    fontSkin="footnote"
                    textColor="subtle"
                    mt={2}
                    style={{ textAlign: 'center' }}
                >
                    {__('Powered by AI. Please double check responses.')}
                </Typography>
            </Box>
        </Box>
    );
}
